import React, { useEffect, useRef, useState } from 'react';
import {
  Box, HStack, Kbd, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,
  Text, useDisclosure, VStack,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import useCurrentUser from '../hooks/useCurrentUser';
import { OPEN_SEARCH_EVENT } from './CommandPalette';

// item 277 — app-wide keyboard shortcuts. A discoverable help overlay (press ?) plus
// Linear-style "g then <key>" navigation. Shortcuts are ignored while typing in an
// input/textarea/select or contentEditable, so they never hijack form entry.
// NOTE: ⌘Z "undo a saved change" (the broad ask) is deliberately NOT here — it needs a
// server-aware action-history/undo-stack (its own item); native ⌘Z still works in fields.

interface NavShortcut { keys: string; label: string; route: string; }

const NAV: NavShortcut[] = [
  { keys: 'd', label: 'Dashboard', route: '/' },
  { keys: 'p', label: 'Proposals', route: '/proposals' },
  { keys: 'i', label: 'Invoices', route: '/Invoices' },
  { keys: 'w', label: 'Work Orders', route: '/WorkOrders' },
  { keys: 'u', label: 'Purchase Orders', route: '/PurchaseOrder' },
  { keys: 'b', label: 'Bills', route: '/bills' },
  { keys: 'c', label: 'CRM / Customers', route: '/customers' },
  { keys: 'm', label: 'My Book', route: '/my-book' },
  { keys: 's', label: 'Sales Bible', route: '/sales-bible' },
];

const isTypingTarget = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
};

const KeyboardShortcuts: React.FC = () => {
  const history = useHistory();
  const { isOutsideSales } = useCurrentUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  // tracks a pending "g" prefix; cleared after the next key or a short timeout
  const goPending = useRef(false);
  const goTimer = useRef<number | undefined>(undefined);
  const [goArmed, setGoArmed] = useState(false);

  useEffect(() => {
    // the external rep portal is tiny — no power-nav there
    if (isOutsideSales) return undefined;

    const clearGo = () => {
      goPending.current = false;
      setGoArmed(false);
      if (goTimer.current) window.clearTimeout(goTimer.current);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return; // leave OS/browser combos alone
      if (isTypingTarget(e.target)) return;

      // "?" opens the shortcuts help
      if (e.key === '?') {
        e.preventDefault();
        onOpen();
        return;
      }

      if (goPending.current) {
        const match = NAV.find((n) => n.keys === e.key.toLowerCase());
        clearGo();
        if (match) {
          e.preventDefault();
          history.push(match.route);
        }
        return;
      }

      if (e.key.toLowerCase() === 'g') {
        goPending.current = true;
        setGoArmed(true);
        goTimer.current = window.setTimeout(clearGo, 1500);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (goTimer.current) window.clearTimeout(goTimer.current);
    };
  }, [history, isOutsideSales, onOpen]);

  if (isOutsideSales) return null;

  return (
    <>
      {/* a quiet hint that the "g" prefix is armed, so the sequence is discoverable */}
      {goArmed && (
        <Box position="fixed" bottom={4} left={4} zIndex={1500} bg="blackAlpha.800" color="white" px={3} py={1.5} borderRadius="md" fontSize="sm">
          <Kbd>g</Kbd>
          {' '}
          then a key… (
          <Kbd>?</Kbd>
          {' '}
          for all)
        </Box>
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Keyboard shortcuts</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <Text>Search anything</Text>
                <HStack spacing={1}>
                  <Kbd>⌘</Kbd>
                  <Kbd>K</Kbd>
                </HStack>
              </HStack>
              <HStack justify="space-between">
                <Text>This help</Text>
                <Kbd>?</Kbd>
              </HStack>
              <Box pt={2}>
                <Text fontSize="sm" fontWeight="bold" letterSpacing="0.08em" opacity={0.6} mb={2}>
                  GO TO — press
                  {' '}
                  <Kbd>g</Kbd>
                  {' '}
                  then…
                </Text>
                <VStack align="stretch" spacing={2}>
                  {NAV.map((n) => (
                    <HStack key={n.keys} justify="space-between">
                      <Text>{n.label}</Text>
                      <HStack spacing={1}>
                        <Kbd>g</Kbd>
                        <Kbd>{n.keys}</Kbd>
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default KeyboardShortcuts;
